import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Kicker, Reveal } from "../components/Reveal";
import { api, apiError } from "../lib/api";

const AMOUNTS = [50, 100, 150, 200, 250];
const input = "mt-1.5 w-full rounded-md border border-linen bg-ivory px-3 py-2.5 text-sm outline-none focus:border-forest";
const label = "text-xs font-semibold uppercase tracking-[0.15em] text-moss";

export default function GiftPage() {
  const navigate = useNavigate();
  const [menu, setMenu] = useState([]);
  const [kind, setKind] = useState("amount");
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [form, setForm] = useState({ purchaser_name: "", purchaser_email: "", recipient_name: "", recipient_email: "", message: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/menu").then((res) => {
      setMenu(res.data);
      const first = res.data.flatMap((g) => g.items).find((i) => i.price > 0);
      if (first) setServiceId(first.id);
    }).catch(() => setMenu([]));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const finalAmount = custom ? Number(custom) : amount;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = { ...form, recipient_email: form.recipient_email || null, kind, amount: kind === "amount" ? finalAmount : null, service_id: kind === "service" ? serviceId : null };
      const { data } = await api.post("/gift-certificates", payload);
      navigate(`/gift/${data.id}`);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="gift-page" className="bg-ivory px-6 pb-28 pt-16 sm:px-8">
      <div className="mx-auto grid max-w-6xl gap-14 lg:grid-cols-5">
        <Reveal className="lg:col-span-2">
          <Kicker>Gift Certificates</Kicker>
          <h1 className="mt-4 font-serif text-4xl font-medium leading-tight tracking-tight text-forest sm:text-5xl">Give the gift of Releaf</h1>
          <p className="mt-5 text-sm leading-relaxed text-moss sm:text-base">
            Choose an amount or a specific treatment, add a note, and we&rsquo;ll create a printable certificate instantly.
          </p>
          <ol className="mt-8 space-y-4 text-sm text-moss">
            <li className="flex gap-3"><span className="font-serif text-xl text-forest">1</span> Fill out the details below.</li>
            <li className="flex gap-3"><span className="font-serif text-xl text-forest">2</span> Print or share the certificate link.</li>
            <li className="flex gap-3"><span className="font-serif text-xl text-forest">3</span> Pay at Salon 406 or by phone at 801-797-7060 — the certificate activates once paid.</li>
          </ol>
        </Reveal>

        <Reveal delay={0.05} className="lg:col-span-3">
          <form onSubmit={submit} className="rounded-lg border border-linen p-7 sm:p-9">
            <div className="flex rounded-md border border-linen p-1 text-sm font-semibold">
              <button type="button" data-testid="gift-kind-amount" onClick={() => setKind("amount")} className={`flex-1 rounded px-4 py-2 transition-colors ${kind === "amount" ? "bg-forest text-ivory" : "text-moss hover:text-forest"}`}>Dollar amount</button>
              <button type="button" data-testid="gift-kind-service" onClick={() => setKind("service")} className={`flex-1 rounded px-4 py-2 transition-colors ${kind === "service" ? "bg-forest text-ivory" : "text-moss hover:text-forest"}`}>Specific treatment</button>
            </div>

            {kind === "amount" ? (
              <div className="mt-6">
                <p className={label}>Amount</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {AMOUNTS.map((a) => (
                    <button key={a} type="button" data-testid={`gift-amount-${a}`} onClick={() => { setAmount(a); setCustom(""); }} className={`rounded-md border px-4 py-2 text-sm font-semibold transition-colors ${!custom && amount === a ? "border-forest bg-forest text-ivory" : "border-linen text-forest hover:border-forest"}`}>${a}</button>
                  ))}
                  <input data-testid="gift-amount-custom" type="number" min="25" max="1000" placeholder="Custom" value={custom} onChange={(e) => setCustom(e.target.value)} className="w-28 rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-forest" />
                </div>
              </div>
            ) : (
              <div className="mt-6">
                <label htmlFor="gift-service" className={label}>Treatment</label>
                <select id="gift-service" data-testid="gift-service-select" value={serviceId} onChange={(e) => setServiceId(e.target.value)} className={input}>
                  {menu.map((g) => (
                    <optgroup key={g.category} label={g.category}>
                      {g.items.filter((i) => i.price > 0).map((i) => <option key={i.id} value={i.id}>{i.name} — ${i.price}{i.unit ? ` ${i.unit}` : ""}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
            )}

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div><label className={label}>Recipient name</label><input data-testid="gift-recipient-name" required value={form.recipient_name} onChange={set("recipient_name")} className={input} /></div>
              <div><label className={label}>Recipient email (optional)</label><input data-testid="gift-recipient-email" type="email" value={form.recipient_email} onChange={set("recipient_email")} className={input} /></div>
              <div><label className={label}>Your name</label><input data-testid="gift-purchaser-name" required value={form.purchaser_name} onChange={set("purchaser_name")} className={input} /></div>
              <div><label className={label}>Your email</label><input data-testid="gift-purchaser-email" type="email" required value={form.purchaser_email} onChange={set("purchaser_email")} className={input} /></div>
            </div>
            <div className="mt-4">
              <label className={label}>Personal message (optional)</label>
              <textarea data-testid="gift-message" rows={3} maxLength={500} value={form.message} onChange={set("message")} placeholder="Happy birthday! Treat yourself." className={input} />
            </div>

            {error && <p data-testid="gift-error" className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</p>}

            <button type="submit" data-testid="gift-submit" disabled={busy || (kind === "amount" && (finalAmount < 25 || finalAmount > 1000))} className="mt-6 w-full rounded-md bg-forest px-6 py-3.5 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark disabled:opacity-50">
              {busy ? "Creating..." : "Create Gift Certificate"}
            </button>
            <p className="mt-3 text-center text-xs text-moss">No payment online — you&rsquo;ll pay at the salon or by phone.</p>
          </form>
        </Reveal>
      </div>
    </main>
  );
}
